import { Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconFileText } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { isNullish } from 'remeda';
import type { TemplateV2 } from 'shared/hooks/use-load-template-v2';
import { StatementTypeModal } from './StatementTypeModal';
import {
  canSelectStatementType,
  getStatementTypeOptions,
  type StatementType,
  type StatementTypeOption,
} from './statement-types';

const readStatementType = (
  template: TemplateV2,
  options: ReadonlyArray<StatementTypeOption>,
): StatementType | null => {
  const groupedType =
    template.bodies.email[0]?.groupedType ?? template.bodies.letter[0]?.groupedType;
  return options.find(({ value }) => value === groupedType)?.value ?? null;
};

type StatementTypeSelectorProps = {
  template: TemplateV2;
};

export const StatementTypeSelector = ({ template }: StatementTypeSelectorProps) => {
  const { t } = useTranslation();

  if (!canSelectStatementType(template)) return null;

  const options = getStatementTypeOptions(template);
  const currentValue = readStatementType(template, options);
  const currentOption = options.find(({ value }) => value === currentValue);
  const currentLabel = isNullish(currentOption)
    ? t(i18nKeys.NO_STATEMENT)
    : t(currentOption.titleKey);

  const openStatementModal = () =>
    modals.open({
      title: t(i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TITLE),
      size: 'lg',
      children: (
        <StatementTypeModal
          templateId={template.id}
          currentValue={currentValue}
          options={options}
          onClose={modals.closeAll}
        />
      ),
    });

  return (
    <Button
      variant="default"
      size="xs"
      leftSection={<IconFileText size={16} stroke={1.5} />}
      onClick={openStatementModal}
    >
      {currentLabel}
    </Button>
  );
};
